import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchAdminSeminarer,
  createSeminar,
  updateSeminar,
  deleteSeminar,
  toggleSeminarVisibility,
  fetchSeminarRegistrations,
} from "../../services/adminApi";

const EMPTY = { title: "", description: "", date: "", time: "", location: "", speaker: "" };

function SeminarManager() {
  const [seminars, setSeminars] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // seminar id being edited
  const [editForm, setEditForm] = useState({});
  const [registrations, setRegistrations] = useState({}); // { [seminarId]: [] }
  const [openRegs, setOpenRegs] = useState({}); // { [seminarId]: bool }

  useEffect(() => {
    fetchAdminSeminarer().then(setSeminars).catch(console.error);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createSeminar(form);
      setSeminars((prev) => [...prev, created]);
      setForm(EMPTY);
      toast.success("Seminar opprettet!");
    } catch (err) {
      toast.error(err.message || "Kunne ikke opprette seminar");
    }
  };

  const handleUpdate = async (id) => {
    try {
      const updated = await updateSeminar(id, editForm);
      setSeminars((prev) => prev.map((s) => (s.id === id ? { ...s, ...updated } : s)));
      setEditing(null);
      toast.success("Seminar oppdatert!");
    } catch (err) {
      toast.error(err.message || "Kunne ikke oppdatere seminar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Slette dette seminaret og alle påmeldinger?")) return;
    try {
      await deleteSeminar(id);
      setSeminars((prev) => prev.filter((s) => s.id !== id));
      toast.info("Seminar slettet");
    } catch (err) {
      toast.error("Kunne ikke slette seminar");
    }
  };

  const handleToggle = async (id, visible) => {
    try {
      const updated = await toggleSeminarVisibility(id, visible);
      setSeminars((prev) => prev.map((s) => (s.id === id ? { ...s, visible: updated.visible } : s)));
      toast.success(visible ? "Seminar er nå synlig" : "Seminar er nå skjult");
    } catch (err) {
      toast.error("Kunne ikke endre synlighet");
    }
  };

  const handleLoadRegs = async (id) => {
    if (openRegs[id]) {
      setOpenRegs((prev) => ({ ...prev, [id]: false }));
      return;
    }
    try {
      const data = await fetchSeminarRegistrations(id);
      setRegistrations((prev) => ({ ...prev, [id]: data }));
      setOpenRegs((prev) => ({ ...prev, [id]: true }));
    } catch (err) {
      toast.error("Kunne ikke laste påmeldinger");
    }
  };

  return (
    <div>
      {/* ─── Create form ─── */}
      <div className="card mb-4">
        <div className="card-header fw-bold">➕ Nytt seminar</div>
        <div className="card-body">
          <form onSubmit={handleCreate}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Tittel *</label>
                <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Dato *</label>
                <input type="date" className="form-control" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Tid *</label>
                <input type="time" className="form-control" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required />
              </div>
              <div className="col-md-6">
                <label className="form-label">Sted</label>
                <input className="form-control" placeholder="F.eks. Al-Yasin lokalet" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Foredragsholder</label>
                <input className="form-control" placeholder="Navn på foredragsholder" value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} />
              </div>
              <div className="col-12">
                <label className="form-label">Beskrivelse</label>
                <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-primary">Opprett seminar</button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ─── Seminar list ─── */}
      {seminars.length === 0 && <p className="text-muted">Ingen seminarer opprettet ennå.</p>}

      {seminars.map((s) => (
        <div key={s.id} className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-semibold">
              {s.title}
              {" "}
              <span className={`badge ms-2 ${s.visible ? "bg-success" : "bg-secondary"}`}>
                {s.visible ? "Synlig" : "Skjult"}
              </span>
            </span>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleToggle(s.id, !s.visible)}>
                {s.visible ? "Skjul" : "Vis"}
              </button>
              <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditing(s.id); setEditForm({ title: s.title, description: s.description, date: s.date, time: s.time, location: s.location || "", speaker: s.speaker || "" }); }}>
                Rediger
              </button>
              <button className="btn btn-sm btn-outline-info" onClick={() => handleLoadRegs(s.id)}>
                {openRegs[s.id] ? "Skjul påmeldinger" : "Vis påmeldinger"}
                {registrations[s.id] && <span className="badge bg-info ms-1">{registrations[s.id].length}</span>}
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(s.id)}>Slett</button>
            </div>
          </div>

          {/* Edit form */}
          {editing === s.id && (
            <div className="card-body border-bottom">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Tittel</label>
                  <input className="form-control" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Dato</label>
                  <input type="date" className="form-control" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tid</label>
                  <input type="time" className="form-control" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Sted</label>
                  <input className="form-control" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Foredragsholder</label>
                  <input className="form-control" value={editForm.speaker} onChange={(e) => setEditForm({ ...editForm, speaker: e.target.value })} />
                </div>
                <div className="col-12">
                  <label className="form-label">Beskrivelse</label>
                  <textarea className="form-control" rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                </div>
                <div className="col-12 d-flex gap-2">
                  <button className="btn btn-success btn-sm" onClick={() => handleUpdate(s.id)}>Lagre</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Avbryt</button>
                </div>
              </div>
            </div>
          )}

          {/* Info summary */}
          {editing !== s.id && (
            <div className="card-body py-2 text-muted small">
              📅 {s.date} &nbsp;🕐 {s.time}
              {s.location && <>&nbsp; 📍 {s.location}</>}
              {s.speaker && <>&nbsp; 🎤 {s.speaker}</>}
              {s.description && <div className="mt-1">{s.description}</div>}
            </div>
          )}

          {/* Registrations table */}
          {openRegs[s.id] && registrations[s.id] && (
            <div className="card-body pt-0">
              {registrations[s.id].length === 0 ? (
                <p className="text-muted small mb-0">Ingen påmeldinger ennå.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Navn</th><th>E-post</th><th>Telefon</th><th>Alder</th><th>Type</th><th>Dato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations[s.id].map((r) => (
                        <tr key={r.id}>
                          <td>{r.name}</td>
                          <td>{r.email}</td>
                          <td>{r.phone || "–"}</td>
                          <td>{r.age_range || "–"}</td>
                          <td><span className={`badge ${r.is_member ? "bg-success" : "bg-secondary"}`}>{r.is_member ? "Medlem" : "Gjest"}</span></td>
                          <td>{new Date(r.created_at).toLocaleDateString("no-NO")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default SeminarManager;
